import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '@/lib/queries.js'
import { Button } from '@/components/ui/button.jsx'
import { Spinner } from '@/components/ui/feedback.jsx'
import { cn } from '@/lib/utils.js'

/**
 * The in-app notification feed (Phase 2, item 19 — D28c). "In-app at
 * minimum" (brief) — this is that minimum: a bell, an unread count, a list.
 *
 * A lightweight custom dropdown rather than the Dialog component: Dialog is
 * a full modal (overlay + focus trap) built for forms and confirmations,
 * heavier than a quick glance at recent activity needs. Closes on an
 * outside click or Escape, same expectations as any popover.
 */
export function NotificationBell () {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef(null)
  const navigate = useNavigate()
  const { data, isPending } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()

  const notifications = data?.notifications ?? []
  const unreadCount = data?.unread_count ?? 0

  React.useEffect(() => {
    if (!open) return
    function onOutside (e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    function onEscape (e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onOutside)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [open])

  async function openNotification (n) {
    setOpen(false)
    if (!n.read) markRead.mutate(n.id)
    if (n.action_url) navigate(n.action_url)
  }

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => setOpen(v => !v)}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        data-testid="notification-bell"
      >
        <span className="relative">
          <Bell />
          {unreadCount > 0 && (
            <span
              className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-danger text-[0.625rem] font-semibold text-white"
              data-testid="notification-unread-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </span>
      </Button>

      {open && (
        <div
          className="absolute right-0 top-full z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-surface shadow-raised"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-sm font-medium text-ink">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="text-xs text-brand hover:underline"
                onClick={() => markAllRead.mutate()}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isPending
              ? <div className="flex justify-center py-6"><Spinner label="Loading" /></div>
              : notifications.length === 0
                ? <p className="px-3 py-6 text-center text-sm text-ink-muted">Nothing yet.</p>
                : (
                  <ul className="divide-y divide-line">
                    {notifications.map(n => (
                      <li key={n.id}>
                        <button
                          type="button"
                          onClick={() => openNotification(n)}
                          className={cn(
                            'flex w-full flex-col gap-0.5 px-3 py-2.5 text-left transition-colors hover:bg-surface-sunken',
                            !n.read && 'bg-brand-subtle/40'
                          )}
                        >
                          <span className="flex items-center gap-2 text-sm font-medium text-ink">
                            {!n.read && <span className="size-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />}
                            {n.title}
                          </span>
                          {n.body && <span className="text-xs text-ink-muted">{n.body}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                  )}
          </div>
        </div>
      )}
    </div>
  )
}
